import type { BadgeVariant } from '../components/ui'

/**
 * ステータス/プラン → Badge variant の写像（Unit 007 StatusBadgeMapping）。
 * 色の「意味」を保持したまま 005 Badge の汎用 variant に対応づける。
 * 画面間でのマッピング分散を防ぐため単一定義に集約する。
 */

// 書き起こしステータス → Badge variant
// pending=待ち(warning) / processing=処理中(info) / completed=完了(success)
// partial=部分(warning) / failed=失敗(danger) / cancelled=中止(neutral)
// quota_exceeded は DB の status ではなく failed からの派生値（下記 classifyFailure 参照）。
// サービス側の不具合ではなく利用者が枠を使い切っただけなので、danger ではなく warning にする。
const STATUS_VARIANT: Record<string, BadgeVariant> = {
  pending: 'warning',
  processing: 'info',
  completed: 'success',
  partial: 'warning',
  failed: 'danger',
  cancelled: 'neutral',
  quota_exceeded: 'warning',
}

export function statusToBadgeVariant(status: string): BadgeVariant {
  return STATUS_VARIANT[status] ?? 'neutral'
}

// 書き起こしステータス → 日本語表示ラベル
// 画面間での表記ゆれ（英語のまま / 表現の不一致）を防ぐため単一定義に集約する。
const STATUS_LABEL: Record<string, string> = {
  pending: '待機中',
  processing: '処理中',
  completed: '完了',
  partial: '部分完了',
  failed: '失敗',
  cancelled: 'キャンセル',
  quota_exceeded: '超過エラー',
}

export function statusToLabel(status: string): string {
  return STATUS_LABEL[status] ?? status
}

/**
 * プラン時間超過メッセージの接頭辞。
 * バックエンドの app/utils/failure_kind.py と同一の文字列で、変更するときは両方を揃える。
 */
export const QUOTA_EXCEEDED_PREFIX = '月間利用時間の上限を超過します'

/**
 * 失敗の種別を表示用の値へ写像する（Unit: 管理画面の超過エラー表示）。
 *
 * transcriptions.status は DB の CHECK 制約で失敗が 'failed' の 1 値に集まるため、
 * プラン時間超過だけを error_message から判別して 'quota_exceeded' に振り替える。
 * 通常は API が failure_kind を返すので、これはその値が無い場合のフォールバック。
 */
export function classifyFailure(status: string, errorMessage?: string | null): string {
  if (status === 'failed' && errorMessage?.includes(QUOTA_EXCEEDED_PREFIX)) {
    return 'quota_exceeded'
  }
  return status
}

// プラン種別 → Badge variant
// free=neutral / lite=info / standard=success / unlimited=warning
// （lite/standard が同色化しないよう重複なく割当。紫トークンは追加しない）
const PLAN_VARIANT: Record<string, BadgeVariant> = {
  free: 'neutral',
  lite: 'info',
  standard: 'success',
  unlimited: 'warning',
}

export function planToBadgeVariant(planType: string): BadgeVariant {
  return PLAN_VARIANT[planType] ?? 'neutral'
}
